// Shared canvas compositing for every printable/downloadable QR code in this
// module (the primary customer-level QR in CustomerQrPanel.jsx and each
// per-location QR in CustomerSitesPanel.jsx) - burns a title/subtitle onto
// the raw QR image so a printed sticker can't be mixed up once it's off the
// screen and stuck to a wall at the customer's premises.
export const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Greedy word-wrap against a canvas 2D context, capped at maxLines (customer
// names can run 250-300 characters - see CustomerForm.jsx - so an unbounded
// single line is not an option). The last line is ellipsis-truncated if the
// text still doesn't fit within maxLines.
export const wrapLines = (ctx, text, maxWidth, maxLines) => {
  if (!text) return [];
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (current && ctx.measureText(test).width > maxWidth) {
      lines.push(current);
      current = word;
      if (lines.length === maxLines) break;
    } else {
      current = test;
    }
  }
  if (lines.length < maxLines && current) lines.push(current);
  if (lines.length > maxLines) lines.length = maxLines;
  const fullyConsumed = lines.join(" ").length >= text.length;
  if (!fullyConsumed && lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = `${last}…`;
  }
  return lines;
};

// Composes a raw QR PNG Blob into a bigger canvas: title above (bold, up to
// 3 lines), the QR code, subtitle below (up to 2 lines). Returns an object
// URL to the composed PNG - caller owns revoking it.
export const composeQrImage = async (blob, { title, subtitle } = {}) => {
  const rawUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImage(rawUrl);

    const qrSize = 240;
    const padding = 16;
    const gap = 8;
    const titleLineHeight = 20;
    const subtitleLineHeight = 16;

    const measure = document.createElement("canvas").getContext("2d");
    measure.font = "bold 16px Arial";
    const titleLines = wrapLines(measure, title, qrSize, 3);
    measure.font = "13px Arial";
    const subtitleLines = wrapLines(measure, subtitle, qrSize, 2);

    const headerHeight = titleLines.length * titleLineHeight;
    const footerHeight = subtitleLines.length ? subtitleLines.length * subtitleLineHeight + gap : 0;

    const canvas = document.createElement("canvas");
    canvas.width = qrSize + padding * 2;
    canvas.height = padding + headerHeight + gap + qrSize + footerHeight + padding;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#000000";
    ctx.textAlign = "center";

    ctx.font = "bold 16px Arial";
    let y = padding + titleLineHeight - 4;
    titleLines.forEach((line) => {
      ctx.fillText(line, canvas.width / 2, y);
      y += titleLineHeight;
    });

    const qrY = padding + headerHeight + gap;
    ctx.drawImage(img, padding, qrY, qrSize, qrSize);

    if (subtitleLines.length) {
      ctx.font = "13px Arial";
      let ly = qrY + qrSize + gap + subtitleLineHeight - 4;
      subtitleLines.forEach((line) => {
        ctx.fillText(line, canvas.width / 2, ly);
        ly += subtitleLineHeight;
      });
    }

    return await new Promise((resolve) => {
      canvas.toBlob((composedBlob) => resolve(URL.createObjectURL(composedBlob)), "image/png");
    });
  } finally {
    URL.revokeObjectURL(rawUrl);
  }
};

export const printComposedImage = (url, title) => {
  const win = window.open("", "_blank", "width=420,height=560");
  if (!win) return;
  win.document.write(
    `<html><head><title>QR - ${title}</title></head><body style="text-align:center;margin-top:40px;">` +
      `<img src="${url}" style="width:300px;" onload="window.print();window.close();" />` +
      `</body></html>`
  );
  win.document.close();
};
